import { useCallback, useEffect, useRef, useState } from 'react';
import {
  OutcomeView,
  toOutcome,
  useSession,
  type ModuleScreenContext,
  type Outcome,
} from '@huaca/ui';
import { getFacets, listPostings } from './api';
import { ActiveFilters } from './ActiveFilters';
import { Filters } from './Filters';
import { PostingCard } from './PostingCard';
import { ResultsTable } from './ResultsTable';
import { ViewSwitch } from './ViewSwitch';
import { SORT_LABELS } from './labels';
import { ResultsSkeleton } from './Skeleton';
import { effectiveDirection, effectiveSort, toSearchParams, useSearchState } from './search-state';
import { readViewMode, writeViewMode, type ViewMode } from './view-mode';
import type { FacetCounts, PostingsPage, Sort, SortDirection } from './types';

/** Typing is continuous; everything else is a deliberate act. Only the former is worth debouncing. */
const TYPING_DEBOUNCE_MS = 300;

export function SearchPage({ ctx }: { ctx: ModuleScreenContext }) {
  const { client } = useSession();
  const { query, setFilter, goToPage, canGoPrevious } = useSearchState('/');

  const [page, setPage] = useState<PostingsPage | null>(null);
  const [facets, setFacets] = useState<FacetCounts | null>(null);
  /**
   * The total, REMEMBERED ACROSS PAGE TURNS.
   *
   * The server sends it on the first page only — paging must not re-count, and the count cannot
   * change under a cursor anyway. Held here because it is invariant under exactly the same changes
   * as the facet counts: turn a page and it still holds, change a filter and it must not. Without
   * this, page 2 fell back to `items.length` and announced "20 תוצאות" for a 200-result search.
   */
  const [total, setTotal] = useState<number | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * The view preference. Read from storage LAZILY — as an initialiser, not a call — so the throw a
   * private window raises happens inside `readViewMode`'s own try, once, rather than on every
   * render of this component.
   */
  const [view, setView] = useState<ViewMode>(readViewMode);
  const chooseView = useCallback((next: ViewMode) => {
    setView(next);
    writeViewMode(next);
  }, []);

  const params = toSearchParams(query).toString();
  /** Facet counts do not change when you turn a page, so paging must not re-fetch (or re-bill) them. */
  const facetParams = toSearchParams({ ...query, page: 1 }).toString();

  const previousQ = useRef(query.q);
  const previousFacetParams = useRef<string | null>(null);
  /** Guards against a slow first request overwriting a fast second one. */
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setOutcome(null);

    const wantFacets = previousFacetParams.current !== facetParams;

    try {
      const search = new URLSearchParams(params);
      const [postings, counts] = await Promise.all([
        listPostings(client, search),
        wantFacets ? getFacets(client, new URLSearchParams(facetParams)) : Promise.resolve(null),
      ]);
      if (seq !== requestSeq.current) return;

      setPage(postings.page);
      // `wantFacets` is already the test for "the filters changed, not just the page", so it is the
      // right moment to adopt a fresh total — including a null one, which must not leave a stale
      // number from the previous search on screen.
      if (wantFacets || postings.page.total !== null) setTotal(postings.page.total);
      if (counts) {
        setFacets(counts);
        previousFacetParams.current = facetParams;
      }
    } catch (err) {
      if (seq !== requestSeq.current) return;
      const result = toOutcome(err);

      /**
       * A page the server will not serve costs the reader nothing: drop back to the first page of
       * the same search. The text path caps how deep it will skip, so a bookmarked deep link into a
       * search that has since narrowed is a real case rather than a defensive one.
       */
      if (result.kind === 'invalid' && query.page > 1) {
        setFilter({}, { replace: true });
        return;
      }

      /**
       * The running quota METER was removed from this screen: it is a number nobody is here to
       * read, and it took a third of the toolbar from the search box. Hitting the limit is still
       * reported — `OutcomeView` renders the quota-exceeded state from `outcome`, which carries the
       * limit and the reset time — so the information appears exactly when it becomes actionable.
       */
      setOutcome(result);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [client, facetParams, params, query.page, setFilter]);

  useEffect(() => {
    /**
     * Only TYPING is debounced. A facet click or a page turn fires immediately — waiting 300 ms on a
     * deliberate click reads as lag. The URL itself was already updated on the keystroke, so the
     * page has ALREADY reset to the first page by the time this timer starts; the debounce delays
     * the request, never the reset.
     */
    const typed = previousQ.current !== query.q;
    previousQ.current = query.q;

    const timer = setTimeout(() => void load(), typed ? TYPING_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [load, query.q]);

  const textSearchActive = query.q !== '';
  const items = page?.items ?? [];

  /**
   * Open a posting, carrying the search with it.
   *
   * The list's query rides along so the posting page can offer a way back to the EXACT search
   * rather than dumping the reader at an unfiltered page one. Defined once because both views need
   * it: a card and a table row that navigated differently would be the same bug twice.
   */
  /**
   * How many pages there are, when the server could say.
   *
   * `total` is nullable on the text path — Atlas can decline to report a count — and `null` here
   * means the position line is simply omitted rather than rendered as "page 2 of 0". `hasMore`
   * still drives the Next button either way, so paging works with or without it.
   */
  const pageCount =
    page && page.total !== null ? Math.max(1, Math.ceil(page.total / page.pageSize)) : null;

  /**
   * Click a sortable header: switch to that key, or FLIP the direction if it is already the key in
   * force. Setting the key alone would make a second click a no-op, which reads as a broken control.
   */
  const sortBy = useCallback(
    (sort: Sort, direction: SortDirection) => setFilter({ sort, dir: direction }),
    [setFilter],
  );

  const openPosting = useCallback(
    (item: (typeof items)[number]) =>
      ctx.navigate(`/postings/${item.slug}/${item.siteId}${params ? `?${params}` : ''}`),
    [ctx, params],
  );

  /**
   * FIRST load only — `page` is null exactly once. A later refetch keeps the previous results on
   * screen (nothing clears `page`), so a filter change or a page turn never blanks the list; only
   * the very first paint has nothing to show, and an empty table under the word "טוען" is a worse
   * answer than the shape of the thing that is coming.
   */
  const firstLoad = page === null;

  return (
    <div className="tenders-page">
      {/* The search alone. The sort moved down to the status bar, where it sits on the same line
          as the count it qualifies — and the toolbar stopped being a two-column grid, so the box
          gets the full width. */}
      <header className="tenders-toolbar">
        <label className="tenders-search">
          <span className="huaca-visually-hidden">חיפוש</span>
          <input
            type="search"
            value={query.q}
            placeholder="חיפוש חופשי במכרזים ובמשרות"
            // `replace`, so a typed word does not bury the previous screen under eight history
            // entries and leave Back meaning nothing.
            onChange={(e) => setFilter({ q: e.target.value }, { replace: true })}
          />
        </label>
      </header>

      <OutcomeView outcome={outcome} onShowPlans={ctx.showPlans} onRetry={() => void load()}>
        {firstLoad ? (
          <ResultsSkeleton />
        ) : (
        <>
          {/**
            * The answer, then what qualifies it — in ONE full-width row above the grid.
            *
            * Its position is load-bearing, not cosmetic: this used to sit inside `.tenders-results`,
            * which pushed the results column down by its own height while the filter card beside it
            * started at the top. Above the grid, both columns begin on the same line.
            *
            * The count reads first because it is the answer; the chips only qualify it.
            */}
          <div className="tenders-statusbar">
            {/* The number in its own element so it can carry the weight — as one flat sentence it
                rendered at the same size and colour as the chips beside it and read as a label. */}
            <p className="tenders-count" role="status">
              {total !== null ? (
                <>
                  <strong>{total}</strong>
                  {total === 1 ? 'תוצאה' : 'תוצאות'}
                </>
              ) : (
                /**
                 * No total to state. The leading "מציג" is load-bearing: it says what is ON SCREEN,
                 * where a bare number in the same position would read as the size of the whole
                 * result set. Only the known-total branch is allowed to make that claim.
                 */
                <>
                  מציג <strong>{items.length}</strong>
                  {items.length === 1 ? 'תוצאה' : 'תוצאות'}
                </>
              )}
            </p>
            <ActiveFilters query={query} setFilter={setFilter} />

            {/* Pushed to the far end: the count and the chips are the answer, this is how to read
                it. `margin-inline-start: auto` on the wrapper, so it stays put as chips wrap. */}
            <div className="tenders-statusbar__end">
              {/**
                * In table mode the HEADERS are the sort control, so the select stands down rather than
                * sitting beside them saying the same thing twice.
                *
                * With ONE exception, and it is not a hedge: `relevance` has no column — it is offered
                * only while a text query is present, and no header can express it. Hiding the select
                * unconditionally would strand a reader who ranked a search and then switched to the
                * table, with no way back to that ranking short of leaving the view.
                */}
              {(view === 'cards' || textSearchActive) && (
              <label className="tenders-field">
                <span>מיון</span>
                <select
                  // Enabled during a text search now that the server can honour it on both paths. It used
                  // to be disabled because the ETL's search index typed the deadline as a `date` over an
                  // ISO STRING, so Atlas could not sort on it at all and a text query silently came back
                  // ranked whatever the user had picked.
                  //
                  // The value is the EFFECTIVE sort, not the stored one: `null` means "no explicit
                  // choice", which reads as relevance while a query is present and closing-soonest
                  // otherwise. Showing the resolved value is what keeps the control honest before the
                  // first response lands.
                  value={effectiveSort(query)}
                  onChange={(e) => setFilter({ sort: e.target.value as Sort })}
                >
                  {/* Only offered with something to rank — the server rejects the pair outright. */}
                  {textSearchActive && <option value="relevance">{SORT_LABELS.relevance}</option>}
                  <option value="closing">{SORT_LABELS.closing}</option>
                  <option value="newest">{SORT_LABELS.newest}</option>
                </select>
              </label>
              )}
              <ViewSwitch view={view} onChange={chooseView} />
            </div>
          </div>

          <div className="tenders-layout">
            <Filters query={query} facets={facets} setFilter={setFilter} />

            <section className="tenders-results" aria-busy={loading}>
            {!loading && items.length === 0 && <p>לא נמצאו תוצאות. אפשר לצמצם את הסינון.</p>}

            {/**
              * ONE query, ONE result set, two renderings. `items` is whatever the server last
              * returned — the table does not re-sort or re-filter it, so switching view can never
              * change which postings are on screen or what order they are in.
              */}
            {view === 'table' ? (
              <ResultsTable
                items={items}
                // The RESOLVED sort, so a header is marked correctly before any explicit choice has
                // been made — the same value the select shows for the same reason.
                sort={effectiveSort(query)}
                direction={effectiveDirection(query)}
                onSort={sortBy}
                onOpen={openPosting}
              />
            ) : (
              <ul className="tenders-list">
                {items.map((item) => (
                  <li key={`${item.slug}:${item.siteId}`}>
                    <PostingCard item={item} onOpen={() => openPosting(item)} />
                  </li>
                ))}
              </ul>
            )}

            {/* No page numbers: a keyset cannot know how many pages exist, and in a filtered search
                people narrow the filter rather than jump to page 7. */}
            {/* No page LIST: a filtered search is narrowed rather than jumped through, and a row of
                numbers on a result set that changes with every facet click is noise. The position is
                stated instead, which is what a page number was worth having for. */}
            <nav className="tenders-pager" aria-label="דפדוף">
              <button
                type="button"
                className="btn-ghost"
                disabled={!canGoPrevious}
                onClick={() => goToPage(query.page - 1)}
              >
                הקודם
              </button>

              {pageCount !== null && (
                <span className="tenders-pager__at" aria-live="polite">
                  עמוד {query.page} מתוך {pageCount}
                </span>
              )}

              <button
                type="button"
                className="btn-ghost"
                disabled={!page?.hasMore}
                onClick={() => goToPage(query.page + 1)}
              >
                הבא
              </button>
            </nav>
            </section>
          </div>
        </>
        )}
      </OutcomeView>
    </div>
  );
}
